import React, { useState, useEffect, useContext } from "react";
import { Box, Typography } from "@mui/material";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import InfinityCard from "./InfinityCard";
import NeoButton from "./NeoButton";
import { useCardStore } from "../@shared/store/useCardsStore";
import { MainContext } from "../App";
import { db } from "../@shared/firebase/config";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import CharacterCard from "./CharacterCard";

const SWIPE_THRESHOLD = window.innerWidth * 0.1;

const NewCardsPreview = ({ cards, onComplete }) => {
  const { Theme, deviceType } = useContext(MainContext);
  const { userData } = useAuthStore();
  const [cardsToPreview, setCardsToPreview] = useState([]);
  const [claimMessage, setClaimMessage] = useState("");

  // Motion values
  const x = useMotionValue(0);
  const rotate = useTransform(x, (value) => value / 20);
  const opacity = useMotionValue(1);

  const isMobile = deviceType === "mobile";

  // On mount or when `cards` prop changes
  useEffect(() => {
    setCardsToPreview(cards || []);
  }, [cards]);

  // Handle swipe completion
  const handleSwipeComplete = async () => {
    if (cardsToPreview.length === 0) return;

    const [topCard, ...remainingCards] = cardsToPreview;

    if (topCard?.id) {
      await saveCardToUserCollection(topCard);
    }

    setCardsToPreview(remainingCards);
    x.set(0);
    opacity.set(1);
  };

  const saveCardToUserCollection = async (card) => {
    try {
      if (!userData) return;

      const subCollection =
        card.type === "Infinity" ? "Infinity cards" : "Character cards";
      const cardRef = doc(db, "users", userData.id, subCollection, card.id);
      const docSnap = await getDoc(cardRef);

      if (docSnap.exists()) {
        const currentAmount = docSnap.data().amountOwned || 1;
        await updateDoc(cardRef, { amountOwned: currentAmount + 1, new: true });
      } else {
        await setDoc(cardRef, { ...card, amountOwned: 1, new: true });
      }
    } catch (error) {
      console.error("Error saving card:", error);
    }
  };

  const claimAllCards = async () => {
    try {
      if (!userData) return;

      const logs = await Promise.all(
        cardsToPreview.map(async (card) => {
          const subCollection =
            card.type === "Infinity" ? "Infinity cards" : "Character cards";
          const cardRef = doc(db, "users", userData.id, subCollection, card.id);
          const docSnap = await getDoc(cardRef);
          const amountToAdd = docSnap.data()?.amountOwned || 1;

          if (docSnap.exists()) {
            const currentAmount = docSnap.data()?.amountOwned || 1;
            await updateDoc(cardRef, {
              amountOwned: currentAmount + amountToAdd,
              new: true,
            });
          } else {
            await setDoc(cardRef, {
              ...card,
              amountOwned: amountToAdd,
              new: true,
            });
          }

          return `Claimed ${card.name} (${amountToAdd}) to ${subCollection}`;
        })
      );

      setClaimMessage(logs.join("\n"));
      setCardsToPreview([]);
    } catch (error) {
      console.error("Error claiming cards:", error);
    }
  };

  const onDragEnd = (event, info) => {
    if (Math.abs(info.offset.x) > SWIPE_THRESHOLD) {
      const direction = info.offset.x > 0 ? 1 : -1;
      animate(x, direction * window.innerWidth, { duration: 0.5 });
      animate(opacity, 0, { duration: 0.5 }).then(() => {
        handleSwipeComplete();
      });
    } else {
      animate(x, 0, { type: "spring", stiffness: 300 });
    }
  };

  const progress = useTransform(x, (value) =>
    Math.min(Math.abs(value) / SWIPE_THRESHOLD, 1)
  );
  const secondCardTop = useTransform(progress, (p) => `${3 + (0 - 3) * p}%`);
  const secondCardScale = useTransform(progress, (p) => 1 - 0.03 * (1 - p));
  const secondCardOpacity = useTransform(progress, (p) => 1 - 0.1 * (1 - p));

  return (
    <>
      {cardsToPreview.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            sx={{ fontSize: 22, color: "#555", textAlign: "center", mb: 2 }}
          >
            🎉 All Infinity Cards viewed!
          </Typography>
          <Box
            onClick={onComplete}
            sx={{
              backgroundColor: "#007aff",
              py: 1.5,
              px: 3,
              borderRadius: 2,
              cursor: "pointer",
            }}
          >
            <Typography
              sx={{ color: "#fff", fontSize: 16, fontWeight: "bold" }}
            >
              Done
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "90%",
            height: "100%",
            position: "relative",
          }}
        >
          {cardsToPreview.map((card, i) => {
            const staticStyles = {
              zIndex: 100 - i,
              position: "absolute",
              width: "100%",
              aspectRatio: "2.5/3.5",
              pointerEvents: i === 0 ? "auto" : "none",
            };

            if (i === 0) {
              return (
                <motion.div
                  key={`${card.id}-${i}`}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  onDragEnd={onDragEnd}
                  style={{
                    ...staticStyles,
                    x,
                    rotate,
                    opacity,
                  }}
                >
                  {card?.rarity ? (
                    <CharacterCard
                      {...card}
                      containerHeight={isMobile ? 300 : 500}
                      heightPer={98}
                    />
                  ) : (
                    <InfinityCard
                      {...card}
                      containerHeight={isMobile ? 400 : 500}
                      heightPer={95}
                    />
                  )}
                </motion.div>
              );
            } else {
              return (
                <Box
                  key={`${card.id}-${i}`}
                  sx={{
                    ...staticStyles,
                    top: `${i * 3 + 3 + 20}%`,
                    transform: `scale(${1 - i * 0.03})`,
                    opacity: i <= 3 ? 1 : 0,
                  }}
                >
                  {card?.rarity ? (
                    <CharacterCard
                      {...card}
                      containerHeight={isMobile ? 300 : 500}
                      heightPer={98}
                    />
                  ) : (
                    <InfinityCard
                      {...card}
                      containerHeight={isMobile ? 400 : 500}
                      heightPer={95}
                    />
                  )}
                </Box>
              );
            }
          })}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              width: "100%",
              px: 2,
              mt: 2,
              position: "absolute",
              bottom: "-12%",
              left: 0,
              zIndex: 200,
            }}
          >
            <NeoButton
              text="Claim All"
              onClick={claimAllCards}
              width={80}
              color={Theme.palette.primary.main}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default NewCardsPreview;
